/**
 * TermsViewPopup — 약관 보기 전용 팝업.
 * 회원가입 모달 위에 별도 모달로 렌더링. 회원가입 모달은 뒤에 그대로 보임.
 * 본문은 발주처와 협의 후 작성 예정 — 현재는 placeholder.
 */

import { useState, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDraggable } from "../../hooks/useDraggable";
import { useEscClose } from "../../hooks/useEscClose";
import { usePopupTopOffset } from "../../hooks/usePopupTopOffset";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

interface TermsViewPopupProps {
  onClose: () => void;
}

export default function TermsViewPopup({ onClose }: TermsViewPopupProps) {
  const { t } = useTranslation();
  const [closing, setClosing] = useState(false);
  const { position, onMouseDown, prepareClose } = useDraggable();
  const popupTop = usePopupTopOffset();

  const handleClose = () => {
    prepareClose();
    setClosing(true);
    setTimeout(onClose, 400);
  };
  useEscClose(handleClose);
  const containerRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(containerRef);

  return (
    <div
      ref={containerRef}
      className="fixed left-0 right-0 bottom-0 z-[130] flex items-center justify-center"
      style={{ top: popupTop }}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/40" style={{ animation: closing ? "fadeInOverlay 0.3s ease-out reverse forwards" : "fadeInOverlay 0.3s ease-out forwards" }} />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          animation: closing ? "popupOut 0.3s ease-in forwards" : "popupIn 0.3s ease-out forwards",
        }}
      >
        <div className="p-6">
          {/* Header — 좌측 백버튼 + 타이틀 */}
          <div className="pb-3 mb-4 border-b border-slate-200 flex items-center gap-2" onMouseDown={onMouseDown} style={{ cursor: "move" }}>
            <button
              onClick={handleClose}
              className="rounded-full p-1.5 text-sky-600 transition-colors hover:bg-sky-500/10"
              aria-label={t("signup.back") || "뒤로"}
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
            </button>
            <h2 className="text-lg font-bold text-slate-900">
              {t("signup.viewTerms") || "약관보기"}
            </h2>
          </div>

          {/* Body — 본문 placeholder */}
          <div className="min-h-[380px] max-h-[55vh] overflow-y-auto pr-1 flex items-center justify-center text-center text-sm text-slate-500">
            {t("signup.termsPreparing") || "약관 준비 중입니다."}
          </div>
        </div>
      </div>
    </div>
  );
}
